import { registerDestructor } from '@ember/destroyable';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { trackedMap } from '@ember/reactive/collections';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import {
  PrismButton,
  PrismCard,
  PrismIcon,
  PrismMessage,
  PrismTable,
} from '@mylighthouse/prism-ember';
import type { Option } from '@mylighthouse/prism-ember/types/components/prism/select-input';
import type {
  CustomEmberTableColumn,
  CustomEmberTableRow,
} from '@mylighthouse/prism-ember/types/components/prism/table';
// @ts-expect-error: Incorrect type
import InfiniteScroller from '@zestia/ember-simple-infinite-scroller/components/infinite-scroller';
import dayjs from 'dayjs';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import { and, not } from 'ember-truth-helpers';
import GeneralTablesCellsTdTwoLine from 'lumen/components/general/tables/cells/td-two-line';
import SettingsPageTemplate from 'lumen/components/settings/page-template';
import InlineFilters from 'lumen/components/settings/users/inline-filters';
import type { SubscriptionOption } from 'lumen/components/settings/users/subscription-select';
import BulkActionRow from 'lumen/components/settings/users/tables/bulk-action-row';
import CellActions from 'lumen/components/settings/users/tables/cells/cell-actions';
import CellProductAccess from 'lumen/components/settings/users/tables/cells/cell-product-access';
import CellRole from 'lumen/components/settings/users/tables/cells/cell-role';
import CellRoleReadOnly from 'lumen/components/settings/users/tables/cells/cell-role-read-only';
import type ManagedUser from 'lumen/models/managed-user';
import type {
  default as SubscriptionUser,
  UserProducts,
} from 'lumen/models/subscription-user';
import GetElement from 'lumen/modifiers/get-element';
import OnValuesChange from 'lumen/modifiers/on-values-change';
import {
  ACCESS_RIGHTS,
  type AccessRight,
  MIXED,
  type MixedRole,
  PRODUCTS,
} from 'lumen/utils/constants/users-constants';
import {
  setupUnsavedChangesTransitionHandler,
  type TransitionHandler,
} from 'lumen/utils/unsaved-changes-transition-handlers';

const COLUMNS = {
  NAME: 'name',
  ROLE: 'role',
  RATE_AND_MARKET_INSIGHT: 'rateAndMarketInsight',
  BI_BENCHMARKING: 'biBenchmarking',
  DATE_ADDED: 'dateAdded',
  SUBSCRIPTION_COUNT: 'subscriptionCount',
  LAST_ACTIVE: 'lastActive',
  ACTIONS: 'actions',
} as const;

function hasProduct(
  products: UserProducts | null,
  product: keyof UserProducts,
): boolean {
  return products?.[product]?.is_accessible ?? false;
}

function isProductEditable(
  products: UserProducts | null,
  product: keyof UserProducts,
): boolean {
  return products?.[product]?.is_editable ?? false;
}

function toProductAccessState(hasAccess: boolean, isLocked: boolean): string {
  if (isLocked) {
    return hasAccess ? 'readonly' : 'unavailable';
  }
  return hasAccess ? 'checked' : 'unchecked';
}

export interface UserChange {
  email: string;
  name: string;
  newBiBenchmarking: boolean;
  newRateAndMarketInsight: boolean;
  newRole: AccessRight;
  originalBiBenchmarking: boolean;
  originalRateAndMarketInsight: boolean;
  originalRole: AccessRight;
  userId: string;
}

export interface BulkApplyChange {
  addProducts?: string[];
  excludedUserIds: number[];
  removeProducts?: string[];
  role?: AccessRight;
  selectedCount: number;
}

export interface BulkDeleteChange {
  excludedUserIds: number[];
  isSelectAllActive: boolean;
  selectedCount: number;
  /** Empty when isSelectAllActive, the selection is filter based then. */
  userIds: number[];
}

interface UserRow {
  biBenchmarking: string;
  dateAdded: string;
  disableCheckboxSelection?: boolean;
  hasBiBenchmarkingAccess: boolean;
  hasRateInsightAccess: boolean;
  isDisabled?: boolean;
  isLocked?: boolean;
  name: { primary: string; secondary: string };
  onProductChange?: (valuePath: string, checked: boolean) => void;
  onRoleChange?: (role: AccessRight) => void;
  rateAndMarketInsight: string;
  role: AccessRight;
  userId: string;
}

interface RowOverride {
  biBenchmarking?: boolean;
  rateAndMarketInsight?: boolean;
  role?: AccessRight;
}

interface UsersTableSignature {
  Args: {
    displaySearchValue: string;
    isAdmin: boolean;
    isAllSubscriptions: boolean;
    isLoading: boolean;
    isLoadingMore?: boolean;
    managedUserCount: number;
    managedUsers: ManagedUser[];
    onBulkApply: (payload: BulkApplyChange) => Promise<boolean>;
    onBulkDelete: (payload: BulkDeleteChange) => Promise<boolean>;
    onDeleteUser: (user: SubscriptionUser) => Promise<void>;
    onInvite: () => void;
    onLoadMore: () => Promise<void>;
    onProductChange: (product: string | null) => void;
    onRoleChange: (role: string | null) => void;
    onSave: (changes: UserChange[]) => Promise<boolean>;
    onSearchChange: (event: Event) => void;
    onSortChange: (
      sorts: { isAscending: boolean; valuePath: string }[],
    ) => void;
    onSubscriptionChange: (option: SubscriptionOption | null) => void;
    product: string | null;
    role: string | null;
    selectedSubscription: SubscriptionOption | null;
    sorts: { isAscending: boolean; valuePath: string }[];
    totalUserCount: number;
    users: SubscriptionUser[];
  };
}

export default class SettingsUsersTablesUsersTable extends Component<UsersTableSignature> {
  @service declare alerting: Services['alerting'];
  @service('hotel-and-market')
  declare hotelAndMarket: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare router: Services['router'];

  @tracked bulkBiBenchmarkingOverride: boolean | undefined;
  @tracked bulkRateAndMarketInsightOverride: boolean | undefined;
  @tracked bulkRoleOverride: AccessRight | undefined;
  @tracked excludedUserIds = new Set<string>();
  @tracked rowSelection: CustomEmberTableRow[] = [];
  @tracked selectAllActive = false;
  @tracked tableScrollEl: HTMLElement | null = null;
  #transitionHandler?: TransitionHandler;
  private rowOverrides = trackedMap<string, RowOverride>();

  bulkDeleteTask = task({ drop: true }, async (): Promise<void> => {
    if (!this.hasSelection) {
      return;
    }

    const userIds = this.selectAllActive
      ? []
      : this.rowSelection.map((row) =>
          Number((row as unknown as UserRow).userId),
        );

    const success = await this.args.onBulkDelete({
      excludedUserIds: [...this.excludedUserIds].map(Number),
      isSelectAllActive: this.selectAllActive,
      selectedCount: this.selectedUserCount,
      userIds,
    });

    if (success) {
      this.resetAll();
    }
  });

  saveAllTask = task({ drop: true }, async (): Promise<void> => {
    if (this.selectAllActive) {
      await this.saveBulkApply();
      return;
    }

    if (this.validationError || this.allChanges.length === 0) {
      return;
    }

    const success = await this.args.onSave(this.allChanges);

    if (success) {
      this.resetAll();
    }
  });

  private get adminColumns(): CustomEmberTableColumn[] {
    return [
      {
        name: this.intl.t('settings.users.column.date_added'),
        valuePath: COLUMNS.DATE_ADDED,
        textAlign: 'left' as const,
        isSortable: false,
        width: 160,
      },
      {
        name: this.intl.t('settings.users.column.name'),
        valuePath: COLUMNS.NAME,
        tdComponent: GeneralTablesCellsTdTwoLine,
        textAlign: 'left' as const,
        width: 260,
      },
      {
        name: this.intl.t('settings.users.column.user_role'),
        valuePath: COLUMNS.ROLE,
        tdComponent: CellRole,
        textAlign: 'left' as const,
        isSortable: false,
        width: 200,
      },
      {
        name: this.intl.t('settings.users.column.rate_insight_market_insight'),
        valuePath: COLUMNS.RATE_AND_MARKET_INSIGHT,
        tdComponent: CellProductAccess,
        textAlign: 'left' as const,
        isSortable: false,
      },
      {
        name: this.intl.t('settings.users.column.bi_benchmarking'),
        valuePath: COLUMNS.BI_BENCHMARKING,
        tdComponent: CellProductAccess,
        textAlign: 'left' as const,
        isSortable: false,
      },
      {
        name: this.intl.t('settings.users.column.last_active'),
        valuePath: COLUMNS.LAST_ACTIVE,
        textAlign: 'left' as const,
        isSortable: false,
        width: 160,
      },
      {
        name: '',
        valuePath: COLUMNS.ACTIONS,
        tdComponent: CellActions,
        textAlign: 'left' as const,
        isSortable: false,
        width: 40,
      },
    ];
  }

  @cached
  get allChanges(): UserChange[] {
    if (this.args.users.length === 0) {
      return [];
    }

    const changes = new Map<string, UserChange>();

    for (const [userId, override] of this.rowOverrides) {
      const user = this.args.users.find(
        (userRecord) => userRecord.id === userId,
      );
      if (!user) {
        continue;
      }

      const originalRole = user.role;
      const originalRateAndMarketInsight = hasProduct(
        user.products,
        PRODUCTS.RATE_INSIGHT,
      );
      const originalBiBenchmarking = hasProduct(
        user.products,
        PRODUCTS.BI_BENCHMARKING,
      );

      changes.set(userId, {
        userId,
        name: user.name,
        email: user.email,
        originalRole,
        newRole: override.role ?? originalRole,
        originalRateAndMarketInsight,
        newRateAndMarketInsight:
          override.rateAndMarketInsight ?? originalRateAndMarketInsight,
        originalBiBenchmarking,
        newBiBenchmarking: override.biBenchmarking ?? originalBiBenchmarking,
      });
    }

    for (const untypedRow of this.rowSelection) {
      const row = untypedRow as unknown as UserRow;
      if (changes.has(row.userId)) {
        continue;
      }

      const originalRole = row.role;
      const newRole = this.bulkRoleOverride ?? originalRole;
      const originalRateAndMarketInsight = row.hasRateInsightAccess;
      const newRateAndMarketInsight =
        this.bulkRateAndMarketInsightOverride ?? originalRateAndMarketInsight;
      const originalBiBenchmarking = row.hasBiBenchmarkingAccess;
      const newBiBenchmarking =
        this.bulkBiBenchmarkingOverride ?? originalBiBenchmarking;

      const hasChange =
        newRole !== originalRole ||
        newRateAndMarketInsight !== originalRateAndMarketInsight ||
        newBiBenchmarking !== originalBiBenchmarking;

      if (hasChange) {
        changes.set(row.userId, {
          userId: row.userId,
          name: row.name.primary,
          email: row.name.secondary,
          originalRole,
          newRole,
          originalRateAndMarketInsight,
          newRateAndMarketInsight,
          originalBiBenchmarking,
          newBiBenchmarking,
        });
      }
    }

    return [...changes.values()];
  }

  get bulkBiBenchmarking(): { checked: boolean; indeterminate: boolean } {
    return this.computeBulkProductState(COLUMNS.BI_BENCHMARKING);
  }

  get bulkRateAndMarketInsight(): { checked: boolean; indeterminate: boolean } {
    return this.computeBulkProductState(COLUMNS.RATE_AND_MARKET_INSIGHT);
  }

  get bulkRole(): { isIndeterminate: boolean; value: AccessRight | MixedRole } {
    if (this.bulkRoleOverride !== undefined) {
      return { isIndeterminate: false, value: this.bulkRoleOverride };
    }

    const roles = this.rowSelection.map(
      (row) => (row as unknown as UserRow).role,
    );
    const uniqueRoles = [...new Set(roles)];
    const firstRole = uniqueRoles[0];

    if (uniqueRoles.length === 1 && firstRole) {
      return { isIndeterminate: false, value: firstRole };
    }

    return { isIndeterminate: true, value: MIXED };
  }

  get columns(): CustomEmberTableColumn[] {
    if (this.args.isAllSubscriptions) {
      return this.managedColumns;
    }

    return this.args.isAdmin ? this.adminColumns : this.viewerColumns;
  }

  get effectiveRowSelection(): CustomEmberTableRow[] {
    if (!this.selectAllActive) {
      return this.rowSelection;
    }

    return this.selectableRows.filter(
      (row) => !this.excludedUserIds.has((row as unknown as UserRow).userId),
    );
  }

  private get hasBulkOverrides(): boolean {
    return (
      this.hasSelection &&
      (this.bulkRoleOverride !== undefined ||
        this.bulkRateAndMarketInsightOverride !== undefined ||
        this.bulkBiBenchmarkingOverride !== undefined)
    );
  }

  get hasPendingChanges(): boolean {
    return this.rowOverrides.size > 0 || this.hasBulkOverrides;
  }

  get hasSelection(): boolean {
    return this.selectAllActive || this.rowSelection.length > 0;
  }

  get hasUnsavedChanges(): boolean {
    return this.hasPendingChanges;
  }

  get isRunning(): boolean {
    return this.saveAllTask.isRunning || this.bulkDeleteTask.isRunning;
  }

  get isSaveDisabled(): boolean {
    return !!this.validationError || this.isRunning || !this.hasPendingChanges;
  }

  private get managedColumns(): CustomEmberTableColumn[] {
    return [
      {
        name: this.intl.t('settings.users.column.name'),
        valuePath: COLUMNS.NAME,
        tdComponent: GeneralTablesCellsTdTwoLine,
        textAlign: 'left' as const,
        width: 260,
      },
      {
        name: this.intl.t('settings.users.column.subscription'),
        valuePath: COLUMNS.SUBSCRIPTION_COUNT,
        textAlign: 'left' as const,
        isSortable: false,
      },
      {
        name: this.intl.t('settings.users.column.last_active'),
        valuePath: COLUMNS.LAST_ACTIVE,
        textAlign: 'left' as const,
        isSortable: false,
        width: 160,
      },
      {
        name: '',
        valuePath: COLUMNS.ACTIONS,
        tdComponent: CellActions,
        textAlign: 'left' as const,
        isSortable: false,
        width: 40,
      },
    ];
  }

  private get managedRows(): CustomEmberTableRow[] {
    return this.args.managedUsers.map((user) => {
      return {
        [COLUMNS.NAME]: {
          primary: user.name,
          secondary: user.email,
        },
        [COLUMNS.SUBSCRIPTION_COUNT]: this.formatSubscriptionCount(
          user.subscriptionCount,
        ),
        [COLUMNS.LAST_ACTIVE]: user.lastLogin
          ? dayjs(user.lastLogin).format('DD MMM YYYY')
          : this.intl.t('general.missing'),
        [COLUMNS.ACTIONS]: '',
        hideRemoveUser: true,
        onViewSubscriptionAccess: () => this.navigateToSubscriptionAccess(user),
      };
    });
  }

  @cached
  get rows(): CustomEmberTableRow[] {
    if (this.args.isAllSubscriptions) {
      return this.managedRows;
    }

    if (this.args.users.length === 0) {
      return [];
    }

    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const component = this;

    return this.args.users.map((user) => {
      const userId = user.id as string;
      const isLocked = user.isLocked ?? false;
      const isMarketLinked =
        !isLocked &&
        (!isProductEditable(user.products, PRODUCTS.RATE_INSIGHT) ||
          !isProductEditable(user.products, PRODUCTS.BI_BENCHMARKING));
      const userRateInsightAccess = hasProduct(
        user.products,
        PRODUCTS.RATE_INSIGHT,
      );
      const userBiBenchmarkingAccess = hasProduct(
        user.products,
        PRODUCTS.BI_BENCHMARKING,
      );

      const override = this.rowOverrides.get(userId);
      const effectiveRole = override?.role ?? user.role;
      const isAdminRole = effectiveRole === ACCESS_RIGHTS.ADMIN;

      const isRateInsightLocked =
        isLocked ||
        isAdminRole ||
        !isProductEditable(user.products, PRODUCTS.RATE_INSIGHT);
      const isBiBenchmarkingLocked =
        isLocked ||
        isAdminRole ||
        !isProductEditable(user.products, PRODUCTS.BI_BENCHMARKING);

      const effectiveRateAndMarketInsight = isAdminRole
        ? true
        : (override?.rateAndMarketInsight ?? userRateInsightAccess);
      const effectiveBiBenchmarking = isAdminRole
        ? true
        : (override?.biBenchmarking ?? userBiBenchmarkingAccess);

      return {
        userId,
        isLocked,
        isMarketLinked,
        hasBiBenchmarkingAccess: userBiBenchmarkingAccess,
        hasRateInsightAccess: userRateInsightAccess,
        originalRole: user.role,
        originalValues: {
          [COLUMNS.RATE_AND_MARKET_INSIGHT]: toProductAccessState(
            userRateInsightAccess,
            isRateInsightLocked,
          ),
          [COLUMNS.BI_BENCHMARKING]: toProductAccessState(
            userBiBenchmarkingAccess,
            isBiBenchmarkingLocked,
          ),
        },
        [COLUMNS.NAME]: {
          primary: user.name,
          secondary: user.email,
        },
        [COLUMNS.ROLE]: effectiveRole,
        [COLUMNS.RATE_AND_MARKET_INSIGHT]: toProductAccessState(
          effectiveRateAndMarketInsight,
          isRateInsightLocked,
        ),
        [COLUMNS.BI_BENCHMARKING]: toProductAccessState(
          effectiveBiBenchmarking,
          isBiBenchmarkingLocked,
        ),
        [COLUMNS.DATE_ADDED]:
          user.createdAt?.format('DD MMM YYYY') ??
          this.intl.t('general.missing'),
        [COLUMNS.LAST_ACTIVE]:
          user.lastLogin?.format('DD MMM YYYY') ??
          this.intl.t('general.missing'),
        [COLUMNS.ACTIONS]: '',
        disableCheckboxSelection: isLocked,
        get isDisabled(): boolean {
          return isLocked || component.hasSelection;
        },
        onDelete: (): void => {
          void this.args.onDeleteUser(user);
        },
        onViewSubscriptionAccess: () => this.navigateToSubscriptionAccess(user),
        onRoleChange: (role: AccessRight): void => {
          if (component.hasSelection) {
            return;
          }
          this.updateRowOverride(userId, { role }, user);
        },
        onProductChange: (valuePath: string, checked: boolean): void => {
          if (component.hasSelection) {
            return;
          }
          const key =
            valuePath === COLUMNS.RATE_AND_MARKET_INSIGHT
              ? 'rateAndMarketInsight'
              : 'biBenchmarking';
          this.updateRowOverride(userId, { [key]: checked }, user);
        },
      };
    });
  }

  private get selectableRows(): CustomEmberTableRow[] {
    return this.rows.filter(
      (row) => !(row as unknown as UserRow).disableCheckboxSelection,
    );
  }

  get selectedUserCount(): number {
    if (this.selectAllActive) {
      return Math.max(this.args.totalUserCount - this.excludedUserIds.size, 0);
    }

    return this.rowSelection.length;
  }

  get validationError(): string | undefined {
    if (this.selectAllActive) {
      const hasProductChange =
        this.bulkRateAndMarketInsightOverride !== undefined ||
        this.bulkBiBenchmarkingOverride !== undefined;
      const hasProductEnabled =
        this.bulkRateAndMarketInsightOverride === true ||
        this.bulkBiBenchmarkingOverride === true;

      return hasProductChange && !hasProductEnabled
        ? this.intl.t('settings.users.edit.min_product_error')
        : undefined;
    }

    const hasInvalidChange = this.allChanges.some(
      (change) => !change.newRateAndMarketInsight && !change.newBiBenchmarking,
    );

    return hasInvalidChange
      ? this.intl.t('settings.users.edit.min_product_error')
      : undefined;
  }

  private get viewerColumns(): CustomEmberTableColumn[] {
    return [
      {
        name: this.intl.t('settings.users.column.name'),
        valuePath: COLUMNS.NAME,
        tdComponent: GeneralTablesCellsTdTwoLine,
        textAlign: 'left' as const,
        width: 260,
      },
      {
        name: this.intl.t('settings.users.column.user_role'),
        valuePath: COLUMNS.ROLE,
        tdComponent: CellRoleReadOnly,
        textAlign: 'left' as const,
        isSortable: false,
        width: 200,
      },
    ];
  }

  constructor(owner: Owner, args: UsersTableSignature['Args']) {
    super(owner, args);

    this.#transitionHandler = setupUnsavedChangesTransitionHandler(
      this.router,
      this,
      this.hotelAndMarket.hotel
        ? 'hotel.settings.users.index'
        : 'market.settings.users.index',
    );

    registerDestructor(this, () => {
      this.#transitionHandler?.cleanup();
      this.#transitionHandler = undefined;
    });
  }

  /**
   * Changing a filter reloads the table from the first page, which drops the
   * selection and every unsaved row edit, so ask before throwing edits away.
   * Declining leaves the filter untouched: the filter inputs render from the
   * arguments this component receives, so they keep showing the applied value.
   */
  private async applyFilterChange(applyChange: () => void): Promise<void> {
    if (this.hasPendingChanges) {
      const confirmed = await this.showDiscardChangesAlert();

      if (!confirmed) {
        return;
      }

      this.discardChanges();
    }

    applyChange();
  }

  private computeBulkProductState(
    column:
      | typeof COLUMNS.RATE_AND_MARKET_INSIGHT
      | typeof COLUMNS.BI_BENCHMARKING,
  ): { checked: boolean; indeterminate: boolean } {
    const bulkOverride =
      column === COLUMNS.RATE_AND_MARKET_INSIGHT
        ? this.bulkRateAndMarketInsightOverride
        : this.bulkBiBenchmarkingOverride;

    if (this.selectAllActive) {
      // Loaded rows are not representative of the whole selection; without an
      // explicit override the state is unknown across all pages.
      if (bulkOverride !== undefined) {
        return { checked: bulkOverride, indeterminate: false };
      }
      return { checked: false, indeterminate: true };
    }

    if (this.rowSelection.length === 0) {
      return { checked: false, indeterminate: false };
    }

    if (bulkOverride !== undefined) {
      return { checked: bulkOverride, indeterminate: false };
    }

    const states = this.rowSelection.map(
      (row) => (row as unknown as UserRow)[column],
    );
    const allChecked = states.every((state) => state === 'checked');
    const allUnchecked = states.every((state) => state === 'unchecked');

    if (allChecked) {
      return { checked: true, indeterminate: false };
    }
    if (allUnchecked) {
      return { checked: false, indeterminate: false };
    }

    return { checked: false, indeterminate: true };
  }

  private formatSubscriptionCount(count: number): string {
    return count > 0
      ? this.intl.t('settings.users.subscription_count', { count })
      : this.intl.t('general.missing');
  }

  private resetAll(): void {
    this.rowOverrides.clear();
    this.rowSelection = [];
    this.selectAllActive = false;
    this.excludedUserIds = new Set();
    this.bulkRoleOverride = undefined;
    this.bulkRateAndMarketInsightOverride = undefined;
    this.bulkBiBenchmarkingOverride = undefined;
  }

  private resetBulkOverrides(): void {
    this.bulkRoleOverride = undefined;
    this.bulkRateAndMarketInsightOverride = undefined;
    this.bulkBiBenchmarkingOverride = undefined;
  }

  private async saveBulkApply(): Promise<void> {
    if (this.validationError || !this.hasPendingChanges) {
      return;
    }

    let addProducts: string[] | undefined;
    let removeProducts: string[] | undefined;

    if (this.bulkRateAndMarketInsightOverride !== undefined) {
      if (this.bulkRateAndMarketInsightOverride) {
        addProducts = [...(addProducts ?? []), PRODUCTS.RATE_INSIGHT];
      } else {
        removeProducts = [...(removeProducts ?? []), PRODUCTS.RATE_INSIGHT];
      }
    }
    if (this.bulkBiBenchmarkingOverride !== undefined) {
      if (this.bulkBiBenchmarkingOverride) {
        addProducts = [...(addProducts ?? []), PRODUCTS.BI_BENCHMARKING];
      } else {
        removeProducts = [...(removeProducts ?? []), PRODUCTS.BI_BENCHMARKING];
      }
    }

    const success = await this.args.onBulkApply({
      role: this.bulkRoleOverride,
      addProducts,
      removeProducts,
      excludedUserIds: [...this.excludedUserIds].map(Number),
      selectedCount: this.selectedUserCount,
    });

    if (success) {
      this.resetAll();
    }
  }

  private updateRowOverride(
    userId: string,
    update: Partial<RowOverride>,
    user: SubscriptionUser,
  ): void {
    const override = { ...this.rowOverrides.get(userId), ...update };
    const userRateInsightAccess = hasProduct(
      user.products,
      PRODUCTS.RATE_INSIGHT,
    );
    const userBiBenchmarkingAccess = hasProduct(
      user.products,
      PRODUCTS.BI_BENCHMARKING,
    );

    if (override.role === user.role) {
      delete override.role;
    }
    if (override.rateAndMarketInsight === userRateInsightAccess) {
      delete override.rateAndMarketInsight;
    }
    if (override.biBenchmarking === userBiBenchmarkingAccess) {
      delete override.biBenchmarking;
    }

    if (Object.keys(override).length > 0) {
      this.rowOverrides.set(userId, override);
    } else {
      this.rowOverrides.delete(userId);
    }
  }

  @action
  cancelAll(): void {
    this.resetAll();
  }

  @action
  discardChanges(): void {
    this.resetAll();
  }

  @action
  handleBulkBiBenchmarkingToggle(): void {
    this.bulkBiBenchmarkingOverride = !this.bulkBiBenchmarking.checked;
  }

  @action
  handleBulkRateAndMarketInsightToggle(): void {
    this.bulkRateAndMarketInsightOverride =
      !this.bulkRateAndMarketInsight.checked;
  }

  @action
  handleBulkRoleChange(option: Option): void {
    this.bulkRoleOverride = option.key as AccessRight;
  }

  @action
  handleFiltersChanged(): void {
    if (this.hasSelection || this.hasPendingChanges) {
      this.resetAll();
    }
  }

  @action
  handleProductFilterChange(product: string | null): void {
    void this.applyFilterChange(() => this.args.onProductChange(product));
  }

  @action
  handleRoleFilterChange(role: string | null): void {
    void this.applyFilterChange(() => this.args.onRoleChange(role));
  }

  @action
  handleRowSelect(
    rowSelection: CustomEmberTableRow | CustomEmberTableRow[],
  ): void {
    if (this.isRunning) {
      return;
    }

    const selectedRows = Array.isArray(rowSelection)
      ? rowSelection
      : [rowSelection];

    if (this.selectAllActive) {
      if (selectedRows.length === 0) {
        // Deselecting every loaded row would leave an invisible active
        // selection (Prism hides the bulk bar), so clear the whole mode.
        this.resetAll();
        return;
      }

      const selectedUserIds = new Set(
        selectedRows.map((row) => (row as unknown as UserRow).userId),
      );
      const excluded = new Set<string>();
      for (const row of this.selectableRows) {
        const rowUserId = (row as unknown as UserRow).userId;
        if (!selectedUserIds.has(rowUserId)) {
          excluded.add(rowUserId);
        }
      }
      this.excludedUserIds = excluded;
      return;
    }

    this.resetBulkOverrides();

    if (selectedRows.length === 0 && this.rowOverrides.size > 0) {
      this.rowOverrides.clear();
    }

    const selectableRows = this.selectableRows;
    const coversAllLoadedRows =
      selectableRows.length > 0 &&
      selectableRows.every((row) => selectedRows.includes(row));

    if (
      coversAllLoadedRows &&
      this.args.totalUserCount > this.args.users.length
    ) {
      this.selectAllActive = true;
      this.excludedUserIds = new Set();
      this.rowSelection = [];
      this.rowOverrides.clear();
      return;
    }

    this.rowSelection = selectedRows;
  }

  @action
  handleSubscriptionFilterChange(option: SubscriptionOption | null): void {
    void this.applyFilterChange(() => this.args.onSubscriptionChange(option));
  }

  @action
  navigateToSubscriptionAccess(user: ManagedUser | SubscriptionUser): void {
    const routeName = this.hotelAndMarket.hotel
      ? 'hotel.settings.users.subscription-access'
      : 'market.settings.users.subscription-access';

    this.router.transitionTo(routeName, {
      queryParams: {
        userId: user.id,
        userEmail: user.email,
      },
    });
  }

  @action
  registerTableContainer(el: HTMLElement): void {
    this.tableScrollEl = el.querySelector<HTMLElement>('.ember-table-overflow');
  }

  @action
  showDiscardChangesAlert(): Promise<boolean> {
    return this.alerting.showConfirmWarningAlert({
      title: this.intl.t('general.unsaved_changes.title'),
      text: this.intl.t('general.unsaved_changes.description'),
      buttons: {
        cancel: { text: this.intl.t('general.cancel') },
        confirm: { text: this.intl.t('general.discard_changes') },
      },
    });
  }

  <template>
    <SettingsPageTemplate
      @description={{t "settings.users.description"}}
      @isFullHeight={{true}}
      @title={{t "settings.users.title"}}
      data-test-id="settings-users-page"
    >
      <:headerAfter>
        <div class="flex items-end gap-200 self-end">
          {{#if this.hasPendingChanges}}
            <PrismButton
              @buttonType="secondary"
              @label={{t "general.cancel"}}
              @onClick={{this.cancelAll}}
            />
            <PrismButton
              @buttonType="primary"
              @isDisabled={{this.isSaveDisabled}}
              @label={{t "settings.users.bulk.save"}}
              @onClick={{this.saveAllTask.perform}}
              data-test-id="bulk-save"
            />
          {{else if @isAdmin}}
            <PrismButton
              @buttonType="primary"
              @iconBefore="plus"
              @label={{t "settings.users.invite_users"}}
              @onClick={{@onInvite}}
            />
          {{/if}}
        </div>
      </:headerAfter>

      <:default>
        <div
          class="flex h-full flex-col"
          {{OnValuesChange
            this.handleFiltersChanged
            @role
            @product
            @selectedSubscription
          }}
        >
          {{#if this.validationError}}
            <div class="mb-200 flex items-center gap-100" role="alert">
              <PrismIcon @color="critical" @name="warning" @size="small" />
              <span class="text-200--medium text-critical-default">
                {{this.validationError}}
              </span>
            </div>
          {{/if}}
          <PrismCard @contentLayout="noPadding" class="min-h-0 flex-1">
            <:content>
              <div class="flex h-full flex-col overflow-hidden">
                <div
                  class="flex shrink-0 items-center justify-end gap-200 px-400 py-300"
                >
                  <InlineFilters
                    @isAllSubscriptions={{@isAllSubscriptions}}
                    @isLoading={{@isLoading}}
                    @onProductChange={{this.handleProductFilterChange}}
                    @onRoleChange={{this.handleRoleFilterChange}}
                    @onSearchChange={{@onSearchChange}}
                    @onSubscriptionChange={{this.handleSubscriptionFilterChange}}
                    @product={{@product}}
                    @role={{@role}}
                    @search={{@displaySearchValue}}
                    @subscription={{@selectedSubscription}}
                    @userCount={{@managedUserCount}}
                  />
                </div>
                <InfiniteScroller
                  @element={{this.tableScrollEl}}
                  @onLoadMore={{@onLoadMore}}
                  @percent={{90}}
                  class="flex min-h-0 flex-1 flex-col"
                >
                  <PrismTable
                    {{GetElement this.registerTableContainer}}
                    @checkboxSelectionMode={{if
                      (and @isAdmin (not @isAllSubscriptions))
                      "multiple"
                      "none"
                    }}
                    @columns={{this.columns}}
                    @isLoading={{@isLoading}}
                    @isLoadingMore={{@isLoadingMore}}
                    @onRowSelect={{this.handleRowSelect}}
                    @onUpdateSorts={{@onSortChange}}
                    @oneCellBulkEditRow={{false}}
                    @rowSelection={{unless
                      @isLoading
                      this.effectiveRowSelection
                    }}
                    @rows={{this.rows}}
                    @sorts={{@sorts}}
                    @widthConstraint="gte-container-slack"
                  >
                    <:bulkEditRow>
                      <BulkActionRow
                        @badgeColspan={{2}}
                        @bulkBiBenchmarking={{this.bulkBiBenchmarking}}
                        @bulkRateAndMarketInsight={{this.bulkRateAndMarketInsight}}
                        @bulkRole={{this.bulkRole}}
                        @isDeleteDisabled={{this.isRunning}}
                        @isSelectAllActive={{this.selectAllActive}}
                        @numberOfSelectedRows={{this.selectedUserCount}}
                        @onBulkBiBenchmarkingToggle={{this.handleBulkBiBenchmarkingToggle}}
                        @onBulkDelete={{this.bulkDeleteTask.perform}}
                        @onBulkRateAndMarketInsightToggle={{this.handleBulkRateAndMarketInsightToggle}}
                        @onBulkRoleChange={{this.handleBulkRoleChange}}
                      />
                    </:bulkEditRow>
                    <:emptyState>
                      <div class="flex h-full items-center justify-center">
                        <PrismMessage
                          @illustration="100x100_search"
                          @title={{t
                            "settings.users.empty.nothing_matches_filters"
                          }}
                        />
                      </div>
                    </:emptyState>
                  </PrismTable>
                </InfiniteScroller>
              </div>
            </:content>
          </PrismCard>
        </div>
      </:default>
    </SettingsPageTemplate>
  </template>
}
